import {
  createDomainApi,
  type ExtractEntityType,
  type ExtractInputType,
  useApiClient,
} from '@skylabs-digital/react-proto-kit';
import { z } from 'zod';
import { templateInputBaseSchema, templateSchema } from '../schemas/template.schema';
import { unwrap } from './unwrap';

export const templatesApi = createDomainApi('templates', templateSchema, templateInputBaseSchema);

export type TemplateEntity = ExtractEntityType<typeof templatesApi>;
export type TemplateInputType = ExtractInputType<typeof templatesApi>;

export const previewResultSchema = z.object({
  bodyHtml: z.string().nullable(),
  bodyText: z.string().nullable(),
});

export interface PreviewResult {
  bodyHtml: string | null;
  bodyText: string | null;
}

export interface PreviewOptions {
  /**
   * Render missing variables as `[var:name]` placeholders instead of
   * surfacing a server error. Defaults to `true` for the editor so users
   * always see something — they only get a hard error for triple-stash,
   * invalid template syntax, or invalid MJML.
   */
  fillMissing?: boolean;
}

export function usePreviewTemplate() {
  const { connector } = useApiClient();

  const preview = async (
    bodyMjml: string,
    variables?: Record<string, unknown>,
    options: PreviewOptions = {},
  ): Promise<PreviewResult> => {
    const response = await connector.post<PreviewResult>('templates/preview', {
      bodyMjml,
      variables: variables ?? {},
      fillMissing: options.fillMissing ?? true,
    });
    return previewResultSchema.parse(unwrap(response));
  };

  return { preview };
}
